


















































































































import { Vue, Component, Watch } from "vue-property-decorator";
import {
  MonthlyReportModel,
  HeaderModel,
  listMonthlyReports,
  listUserEmails,
  uploadMonthlyReport,
  UploadMonthlyReportsRequest,
  deleteMonthlyReport,
  downloadMonthlyReport,
  UserEmail
} from "@/api/MonthlyReports";
import { reaction } from "mobx";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { LoadingStore } from "@/store/loadingStore";
import { Observer } from "mobx-vue";
import userStore from "@/store/userStore";
import notificationStore from "@/store/notificationStore";
import { SendMonthlyReportNotifications } from "@/api/MonthlyReports/SendMonthlyReportNotifications";

@Observer
@Component({
  components: {
    ValidationProvider,
    ValidationObserver
  }
})
export default class MonthlyReportList extends Vue {
  $refs!: {
    form: HTMLElement & { reset: () => void };
  };
  loading = new LoadingStore();
  monthlyReports: MonthlyReportModel[] = [];
  selectedMonthlyReport: MonthlyReportModel | null = null;
  headers: HeaderModel[] = [];
  isUploadMonthlyReportDialogOpen = false;
  isUploadingReport = false;
  isEmailListDialogOpen = false;
  isSendingEmails = false;
  filterDocumentYear: number = new Date().getFullYear();
  dispose?: () => void;

  uploadReportForm = this.getResetUploadReportForm();

  availableEmailList: UserEmail[] = [];

  errorMessages: string[] = [];

  isMonthlyReportAdmin = userStore.isMonthlyReportAdmin();

  mounted() {
    this.fetchData();

    this.dispose = reaction(() => [userStore.activeCouncil], this.fetchData);
  }

  @Watch("isEmailListDialogOpen")
  async getUserEmailList() {
    this.loading.track(async () => {
      if (this.isEmailListDialogOpen) {
        const emails = await listUserEmails();
        const otherEmails = emails.filter(e => e.email !== userStore.user?.profile?.email);
        const currentUserEmail = emails.filter(e => e.email == userStore.user?.profile?.email);
        if (currentUserEmail && currentUserEmail.length > 0) {
          const currentEmail = currentUserEmail[0];
          currentEmail.selected = true;
          this.availableEmailList = [currentEmail, ...otherEmails];
        } else {
          this.availableEmailList = otherEmails;
        }
      }
    }, "getUserEmailList");
  }

  private async fetchData() {
    this.loading.track(async () => {
      try {
        if (userStore.activeCouncil) {
          const request = {
            documentYear: this.filterDocumentYear
          };
          const response = await listMonthlyReports(request);
          this.monthlyReports = response.monthlyReports;
          this.headers = response.headers;
        }
      } catch (error) {
        const typedError = error as Error;
        if (typedError.message.endsWith("code 403")) {
          notificationStore.error("You do not have permission to view the reports");
        } else {
          notificationStore.error("An unexpected error occurred while loading the reports");
        }
      }
    });
  }

  async downloadReport(monthlyReport: MonthlyReportModel) {
    this.loading.track(async () => {
      try {
        if (userStore.activeCouncil) {
          await downloadMonthlyReport({
            monthlyReportId: monthlyReport.id,
            filename: monthlyReport.fileName
          });
        }
      } catch (error) {
        notificationStore.error("An unexpected error occurred while dowloading the report");
      }
    }, "download");
  }

  async uploadReport() {
    try {
      this.isUploadingReport = true;
      const monthlyReport = await uploadMonthlyReport({ ...this.uploadReportForm } as UploadMonthlyReportsRequest);
      this.selectedMonthlyReport = monthlyReport;

      //reload the page with year filter based on the document year of the new uploaded file
      this.filterDocumentYear = this.uploadReportForm.documentYear;
      await this.fetchData();

      this.closeUploadReportDialog();
      notificationStore.success("Monthly Report Uploaded Successfully");
    } catch (error) {
      notificationStore.error("An unexpected error occurred while dowloading the report");
    } finally {
      this.isUploadingReport = false;
      this.isEmailListDialogOpen = true;
    }
  }

  async openEmailListDialog(monthlyReport: MonthlyReportModel) {
    this.selectedMonthlyReport = monthlyReport;
    this.isEmailListDialogOpen = true;
  }

  async SendEmailNotifications() {
    try {
      this.isSendingEmails = true;

      const selectedEmails = this.availableEmailList
        .filter(email => email.selected)
        .map(email => email.email)
        .join(";");

      const request = {
        toList: selectedEmails,
        fileName: this.selectedMonthlyReport?.fileName ?? ""
      };

      await SendMonthlyReportNotifications(request);

      notificationStore.success("Notifications Sent Successfully");
    } catch (error) {
      notificationStore.error("An unexpected error occurred while sending email Notifications");
    } finally {
      this.isSendingEmails = false;
      this.isEmailListDialogOpen = false;
    }
  }

  closeUploadReportDialog() {
    this.$refs.form.reset();
    this.isUploadMonthlyReportDialogOpen = false;
    this.uploadReportForm = this.getResetUploadReportForm();
  }

  closeEmailListDialog() {
    this.isEmailListDialogOpen = false;
  }

  async deleteReport(monthlyReport: MonthlyReportModel) {
    this.loading.track(async () => {
      try {
        if (userStore.activeCouncil && confirm("Are you sure you want to delete the report?")) {
          await deleteMonthlyReport({ monthlyReportId: monthlyReport.id });

          const index = this.monthlyReports.indexOf(monthlyReport);
          this.monthlyReports.splice(index, 1);
        }
      } catch (error) {
        notificationStore.error("An unexpected error occurred while deleting the report");
      }
    }, "delete");
  }

  validateFile() {
    this.errorMessages = [];
    if (this.uploadReportForm.file) {
      const allowedTypes = ["text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
      if (!allowedTypes.includes(this.uploadReportForm.file.type)) {
        this.errorMessages.push("Please upload a valid Excel/CSV file.");
        this.uploadReportForm = this.getResetUploadReportForm();
      } else {
        this.errorMessages = [];
      }
    }
  }

  preventTyping(event: KeyboardEvent) {
    event.preventDefault();
  }

  preventFocus(event: FocusEvent) {
    //workaround to prevent focus on input field, but enable the up/down arrow to change value
    setTimeout(() => {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
    }, 100);
  }

  onFilterYearChange() {
    this.fetchData();
  }

  getResetUploadReportForm() {
    return {
      file: null as File | null,
      documentYear: new Date().getFullYear()
    };
  }

  async destroyed() {
    if (this.dispose) this.dispose();
  }
}
